import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchEmployers,
  fetchEmployerDetail,
  fetchEmployerJobs,
  clearSelectedEmployer,
} from '../../redux/slices/employersSlice';
import TableHelper from '../Shared/TableHelper';
import api from '../../services/api';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import './EmployersManager.css';

const EmployersManager = () => {
  const dispatch = useDispatch();
  const { items, selectedEmployer, employerJobs, error } = useSelector((state) => state.employers);
  
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('');
  const [activeTab, setActiveTab] = useState('customize'); // 'customize' or 'jobs'
  const [saving, setSaving] = useState(false);
  
  const [customData, setCustomData] = useState({
    brand_color: '#d7192a',
    company_description: '',
    website_url: '',
    phone_custom: '',
    main_location: '',
    company_size: '',
  });

  const [pendingReviews, setPendingReviews] = useState([]);

  const loadPendingReviews = async () => {
    try {
      const response = await api.get('/?action=reviews-pending');
      if (response.data.success) setPendingReviews(response.data.data || []);
    } catch (err) {
      // silencioso: no bloquear el panel si falla
    }
  };

  const handleModerateReview = async (reviewId, decision) => {
    try {
      const response = await api.post('/?action=reviews-moderate', { review_id: reviewId, decision });
      if (response.data.success) {
        toast.success(decision === 'approved' ? 'Reseña aprobada' : 'Reseña rechazada');
        setPendingReviews((prev) => prev.filter((r) => r.id !== reviewId));
      } else {
        toast.error(response.data.message || 'Error al moderar');
      }
    } catch (err) {
      toast.error('Error de red');
    }
  };

  useEffect(() => {
    dispatch(fetchEmployers({ q: search, filter }));
  }, [search, filter, dispatch]);

  useEffect(() => {
    loadPendingReviews();
  }, []);

  useEffect(() => {
    if (selectedEmployer) {
      setCustomData({
        brand_color: selectedEmployer.brand_color || '#d7192a',
        company_description: selectedEmployer.company_description || '',
        website_url: selectedEmployer.website_url || '',
        phone_custom: selectedEmployer.phone_custom || '',
        main_location: selectedEmployer.main_location || '',
        company_size: selectedEmployer.company_size || '',
      });
      dispatch(fetchEmployerJobs({ employerId: selectedEmployer.id }));
    }
  }, [selectedEmployer, dispatch]);

  const handleSelectEmployer = (employerId) => {
    dispatch(fetchEmployerDetail({ employerId }));
  };

  const handleCloseDetail = () => {
    dispatch(clearSelectedEmployer());
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCustomData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSaveCustomization = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append('employer_id', selectedEmployer.id);
      formData.append('brand_color', customData.brand_color);
      formData.append('company_description', customData.company_description);
      formData.append('website_url', customData.website_url);
      formData.append('phone_custom', customData.phone_custom);
      formData.append('main_location', customData.main_location);
      formData.append('company_size', customData.company_size);

      const response = await api.post('/?action=employers-customize', formData);
      if (response.data.success) {
        toast.success('Configuración premium guardada correctamente');
        dispatch(fetchEmployers({ q: search, filter })); // refresh table
      } else {
        toast.error(response.data.message || 'Error al guardar');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error al guardar la personalización');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleJobStatus = (jobId, isActive) => {
    Swal.fire({
      title: isActive ? '¿Pausar oferta?' : '¿Activar oferta?',
      text: isActive 
        ? 'La oferta ya no será visible públicamente.' 
        : 'La oferta volverá a ser visible para candidatos.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: isActive ? '#d7192a' : '#28a745',
      cancelButtonColor: '#6c757d',
      confirmButtonText: isActive ? 'Sí, pausar' : 'Sí, activar',
      cancelButtonText: 'Cancelar'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await api.post('/?action=jobs-toggle', { job_id: jobId });
          if (response.data.success) {
            toast.success(isActive ? 'Oferta pausada' : 'Oferta activada');
            dispatch(fetchEmployerJobs({ employerId: selectedEmployer.id }));
          } else {
            toast.error(response.data.message || 'Error al cambiar estado');
          }
        } catch (err) {
          toast.error('Error de red');
        }
      }
    });
  };

  const handleToggleVerified = async (employerId, isVerified) => {
    try {
      const response = await api.post('/?action=employers-verify', { employer_id: employerId });
      if (response.data.success) {
        toast.success(isVerified ? 'Verificación retirada' : 'Empresa verificada correctamente');
        dispatch(fetchEmployerDetail({ employerId }));
        dispatch(fetchEmployers({ q: search, filter }));
      } else {
        toast.error(response.data.message || 'Error al actualizar verificación');
      }
    } catch (err) {
      toast.error('Error de red');
    }
  };

  const columns = useMemo(
    () => [
      {
        Header: 'Empresa',
        accessor: 'name',
        Cell: ({ row: { original: emp } }) => (
          <div className="employer-cell">
            <div 
              className="employer-avatar"
              style={{ borderLeft: `4px solid ${emp.brand_color || '#e0e0e0'}` }}
            >
              {emp.name[0]?.toUpperCase()}
            </div>
            <div className="employer-meta">
              <strong>{emp.name} {Number(emp.verified) === 1 && <span title="Empresa verificada" style={{ color: '#185adb' }}><i className="fas fa-circle-check"></i></span>}</strong>
              <small>{emp.email}</small>
            </div>
          </div>
        ),
      },
      {
        Header: 'Ciudad',
        accessor: 'city',
        Cell: ({ value }) => value || '—',
      },
      {
        Header: 'Anuncios',
        accessor: 'jobs_count',
        Cell: ({ row: { original: emp } }) => (
          <span 
            onClick={() => {
              handleSelectEmployer(emp.id);
              setActiveTab('jobs');
            }}
            style={{ cursor: 'pointer', textDecoration: 'underline' }}
            title="Ver ofertas de esta empresa"
          >
            <strong>{emp.active_jobs_count}</strong> activos / {emp.jobs_count} total
          </span>
        ),
      },
      {
        Header: 'Chats',
        accessor: 'open_chats_count',
        Cell: ({ value }) => (
          <span className={`badge ${value > 0 ? 'active' : ''}`}>
            {value} abiertos
          </span>
        ),
      },
      {
        Header: 'Acciones',
        id: 'actions',
        Cell: ({ row: { original: emp } }) => (
          <div className="actions" style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={() => {
                handleSelectEmployer(emp.id);
                setActiveTab('customize');
              }}
              className="btn btn-sm btn-secondary"
            >
              Configurar 🎯
            </button>
            <button
              onClick={() => {
                handleSelectEmployer(emp.id);
                setActiveTab('jobs');
              }}
              className="btn btn-sm btn-link"
            >
              Ofertas 📋
            </button>
          </div>
        ),
      },
    ],
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );

  return (
    <div className="employers-manager-container">
      <div className="manager-header">
        <h1>Gestor Premium de Empresas</h1>
        <p>Aplica personalizaciones estéticas de marca e interactúa con el perfil de empresas.</p>
      </div>

      {pendingReviews.length > 0 && (
        <div className="pending-reviews-panel">
          <h3><i className="fas fa-star"></i> Reseñas pendientes de moderar ({pendingReviews.length})</h3>
          <div className="pending-reviews-list">
            {pendingReviews.map((review) => (
              <div className="pending-review-card" key={review.id}>
                <div className="pending-review-head">
                  <strong>{review.employer_name}</strong>
                  <span className="pending-review-stars">
                    {'★'.repeat(Number(review.rating))}{'☆'.repeat(5 - Number(review.rating))}
                  </span>
                </div>
                <p className="pending-review-comment">{review.comment || <em>Sin comentario</em>}</p>
                <small>Por {review.candidate_name}</small>
                <div className="pending-review-actions">
                  <button className="btn btn-sm" style={{ background: '#28a745', color: '#fff' }} onClick={() => handleModerateReview(review.id, 'approved')}>
                    <i className="fas fa-check"></i> Aprobar
                  </button>
                  <button className="btn btn-sm" style={{ background: '#dc3545', color: '#fff' }} onClick={() => handleModerateReview(review.id, 'rejected')}>
                    <i className="fas fa-times"></i> Rechazar
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="manager-toolbar">
        <input
          type="text"
          placeholder="Buscar empresa, correo o ciudad..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="search-input"
        />
        <select value={filter} onChange={(e) => setFilter(e.target.value)} className="filter-select">
          <option value="">Todas las empresas</option>
          <option value="with_jobs">Con anuncios</option>
          <option value="active_jobs">Con anuncios activos</option>
        </select>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <div className={`manager-layout ${selectedEmployer ? 'has-detail' : ''}`}>
        <div className="manager-table-container">
          <TableHelper columns={columns} data={items} exportFileName="empresas_premium" />
        </div>

        {selectedEmployer && (
          <aside className="detail-panel">
            <button onClick={handleCloseDetail} className="btn-close">×</button>
            
            <div className="detail-header-card">
              <div 
                className="detail-avatar"
                style={{ backgroundColor: customData.brand_color, color: '#fff' }}
              >
                {selectedEmployer.name[0]?.toUpperCase()}
              </div>
              <h2>{selectedEmployer.name}</h2>
              <span className="badge-premium">Socio Premium 💎</span>
              <button
                type="button"
                className="btn btn-sm"
                style={{
                  marginTop: 10,
                  backgroundColor: Number(selectedEmployer.verified) === 1 ? '#eef4ff' : '#185adb',
                  color: Number(selectedEmployer.verified) === 1 ? '#185adb' : '#fff',
                  border: '1px solid #185adb',
                }}
                onClick={() => handleToggleVerified(selectedEmployer.id, Number(selectedEmployer.verified) === 1)}
              >
                <i className="fas fa-circle-check"></i> {Number(selectedEmployer.verified) === 1 ? 'Quitar verificación' : 'Marcar como verificada'}
              </button>
            </div>

            <div className="tabs-container">
              <button 
                onClick={() => setActiveTab('customize')} 
                className={`tab-btn ${activeTab === 'customize' ? 'active' : ''}`}
              >
                Personalización
              </button>
              <button 
                onClick={() => setActiveTab('jobs')} 
                className={`tab-btn ${activeTab === 'jobs' ? 'active' : ''}`}
              >
                Ofertas ({employerJobs.length})
              </button>
            </div>

            {activeTab === 'customize' ? (
              <form onSubmit={handleSaveCustomization} className="customization-form">
                <div className="form-group">
                  <label htmlFor="brand_color">Color de marca destacada</label>
                  <div className="color-picker-wrapper">
                    <input
                      id="brand_color"
                      type="color"
                      name="brand_color"
                      value={customData.brand_color}
                      onChange={handleInputChange}
                    />
                    <span className="color-code">{customData.brand_color}</span>
                  </div>
                  <small>Define el color temático para las fichas públicas de esta empresa.</small>
                </div>

                <div className="form-group">
                  <label htmlFor="company_description">Descripción destacada</label>
                  <textarea
                    id="company_description"
                    name="company_description"
                    rows="3"
                    value={customData.company_description}
                    onChange={handleInputChange}
                    placeholder="Descripción corporativa para el perfil público..."
                  ></textarea>
                </div>

                <div className="form-group">
                  <label htmlFor="website_url">Página Web corporativa</label>
                  <input
                    id="website_url"
                    type="text"
                    name="website_url"
                    value={customData.website_url}
                    onChange={handleInputChange}
                    placeholder="ej. www.miempresa.com"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="phone_custom">Teléfono exclusivo</label>
                  <input
                    id="phone_custom"
                    type="text"
                    name="phone_custom"
                    value={customData.phone_custom}
                    onChange={handleInputChange}
                    placeholder="ej. +34 912 345 678"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="main_location">Sede Principal</label>
                  <input
                    id="main_location"
                    type="text"
                    name="main_location"
                    value={customData.main_location}
                    onChange={handleInputChange}
                    placeholder="ej. Madrid, España"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="company_size">Tamaño de la Empresa</label>
                  <select
                    id="company_size"
                    name="company_size"
                    value={customData.company_size}
                    onChange={handleInputChange}
                  >
                    <option value="">No especificado</option>
                    <option value="1-10">1 - 10 empleados</option>
                    <option value="11-50">11 - 50 empleados</option>
                    <option value="51-200">51 - 200 empleados</option>
                    <option value="201-500">201 - 500 empleados</option>
                    <option value="500+">Más de 500 empleados</option>
                  </select>
                </div>

                <div className="preview-card-wrapper">
                  <h4>Vista previa de ficha destacada</h4>
                  <div 
                    className="job-card-preview"
                    style={{ borderLeft: `6px solid ${customData.brand_color}` }}
                  >
                    <span className="preview-comp">{selectedEmployer.name}</span>
                    <span className="preview-title">Desarrollador Full-Stack</span>
                    <span className="preview-loc">{customData.main_location || selectedEmployer.city || 'Ubicación'} · {customData.company_size || 'Tamaño'}</span>
                  </div>
                </div>

                <button 
                  type="submit" 
                  disabled={saving} 
                  className="btn btn-primary btn-block"
                  style={{ backgroundColor: customData.brand_color }}
                >
                  {saving ? 'Guardando...' : 'Aplicar configuración Premium'}
                </button>
              </form>
            ) : (
              <div className="employer-jobs-tab">
                <h3>Ofertas de la empresa</h3>
                {employerJobs.length > 0 ? (
                  <div className="mini-jobs-list">
                    {employerJobs.map((job) => (
                      <div key={job.id} className="mini-job-item">
                        <div className="mini-job-info">
                          <strong>{job.title}</strong>
                          <span className="mini-job-meta">
                            {job.city} · {job.active ? 'Activa' : 'Pausada'}
                          </span>
                        </div>
                        <button
                          onClick={() => handleToggleJobStatus(job.id, job.active)}
                          className={`btn btn-sm ${job.active ? 'btn-danger' : 'btn-success'}`}
                        >
                          {job.active ? 'Pausar' : 'Activar'}
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="no-jobs-text">Esta empresa no ha publicado ninguna oferta de empleo aún.</p>
                )}
              </div>
            )}
          </aside>
        )}
      </div>
    </div>
  );
};

export default EmployersManager;
